/**
 * Load Abandonment - Pixiedust Event
 *
 * This script should be placed in the <head> of the document as early as possible so we can send a
 * "load abandonment" event if a user exits the page before any analytic code has had a chance to
 * load and execute.
 *
 * Other Notes:
 * - Keeping it old school ECMAScript so it can be used as is in the head.
 * - Keeping comments out of the body of the function as it could be rendered on the client
 * - Falling back to a sentinel value of '00000000-0000-0000-0000-000000000000' where a uuid4 would
 *   be required.
 * - Some minimal minification done to reduce file size a bit while maintaining readability.
 * - Beacon api support is required as it's the most reliable way to send an event after a document
 *   has been closed.
 * - For simplicity, script will only target pixiedust on prod.
 * - Script execution also exits early if client does not support navigator or performance timing
 *   api.
 * - NextJS hydration will run this function twice. To prevent attaching the event lister twice,
 *   script will also not execute if `__trackAbandons` is already defined.
 * - getCookie regex test:
 *   - https://regex101.com/r/s8Sz0h/1/tests
 *
 * @param  {String} contentLayer.context_page_id
 * @param  {String} contentLayer.context_page_type
 * @param  {String} contentLayer.destination
 * @param  {String} contentLayer.page_edition
 * @param  {String} contentLayer.source
 */
/* eslint-disable */
const loadAbandonment = function (contentLayer) {
  var d = document, w = window, n = navigator, wp = w.performance, getCookie;
  if (w.__trackAbandons || !n || !n.sendBeacon || !wp || !wp.now) return;

  getCookie = function (cookie) {
    var re = new RegExp('(?:(?:^|.*;\\s*)' + cookie + '\\s*\\=\\s*([^;]*).*$)|^.*$');
    return d.cookie.replace(re, '$1');
  };

  w.__trackAbandons = function () {
    d.removeEventListener('visibilitychange', w.__trackAbandons);
    n.sendBeacon('https://pixiedust.buzzfeed.com/events', w.JSON.stringify([{
      client_session_id: getCookie('bf-xdomain-session-uuid') || '00000000-0000-0000-0000-000000000000',
      client_uuid: getCookie('bf_visit') || '00000000-0000-0000-0000-000000000000',
      context_page_id: contentLayer.context_page_id,
      context_page_type: contentLayer.context_page_type,
      destination: contentLayer.destination || 'buzzfeed',
      event_ts: Math.round(w.Date.now() / 1000),
      event_uri: w.location.href,
      event_uuid: '00000000-0000-0000-0000-000000000000',
      metric_name: 'load-abandonment',
      metric_type: 'custom',
      metric_value: wp.now(),
      mode: (w.matchMedia && w.matchMedia('screen and (max-width:51.9rem)').matches) ? 'mobile' : 'desktop',
      page_edition: contentLayer.page_edition,
      page_session_id: '00000000-0000-0000-0000-000000000000',
      referrer_uri: d.referrer,
      source: contentLayer.source,
      type: 'web_performance_metric',
      viewport_size: { width: w.screen.width, height: w.screen.height }
    }]));
  };
  d.addEventListener('visibilitychange', w.__trackAbandons);
};
/* eslint-enable */

/**
 * Returns the string representation of the loadAbandonment function for rendering on the server.
 * @return {string}
 */
export const loadAbandonmentAsString = ({
  context_page_id,
  context_page_type,
  destination = 'buzzfeed',
  page_edition,
  // @TODO: source is required in the PD schema but shouldn't be since it's not used for routing
  source = 'web_bf',
}) => {
  const loadAbandonmentString = `(${loadAbandonment.toString()})(${
    JSON.stringify({
      context_page_id,
      context_page_type,
      destination,
      page_edition,
      source,
    })
  });`;
  /**
   * Remove any white spaces after the characters ;,{}:
   * Also remove two or more consecutive white spaces
   * https://regex101.com/r/ikukPq/3
   */
  return loadAbandonmentString.replace(/([;,{}:])\s+|(\s+){2,}/g, '$1$2');
};

/**
 * Removes the load abandonment listener once pixiedust is loaded or we will be reporting
 * abandonment for loads where we are also reporting a pageview.
 */
export const removeLoadAbandonment = () => {
  if('__trackAbandons' in window) {
    document.removeEventListener('visibilitychange', window.__trackAbandons);
  }
};
